import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Order from '../../../smoothr-web-app-core/models/Order';
import { SmoothrSocketService } from '../../../smoothr-web-app-core/services/smoothr-socket/smoothr-socket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CashDroEvents } from '../../../smoothr-web-app-core/enums/CashDroEvents';
import { PaySessionStatistics } from '../../../smoothr-web-app-core/models/PaySessionStatistics';
import { interval, takeWhile } from 'rxjs';
import dayjs from 'dayjs';
import { TipSelectorValue, TipType } from '../../components/tip-selector/tip-selector.component';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { TranslateService } from '@ngx-translate/core';
import PiggyGiftCard from '../../../smoothr-web-app-core/models/PiggyGiftCard';
import { PaymentMethod } from '../../../smoothr-web-app-core/enums/PaymentMethod';

export enum CashPaymentResult {
	SUCCESS,
	FAILED,
	CANCELLED,
	TIMEOUT,
	CHECK_AVAILABILITY_FAILED,
}

@Component({
	selector: 'app-cash-payment-modal',
	standalone: false,
	templateUrl: './cash-payment-modal.component.html',
	styleUrls: ['./cash-payment-modal.component.scss'],
})
export class CashPaymentModalComponent implements OnInit {
	order: Order | null = null;
	paySessionStatistics: PaySessionStatistics | null = null;
	cashDroEvent: CashDroEvents | null = null;
	cashDroHint = '';
	giftCards: PiggyGiftCard[] = [];
	qrData: string = '{}';
	cancelEnabled = true;
	timeoutIn: string = '15:00';
	totalPrice = 0;
	formattedTotalPrice = '';
	formattedTips = '';
	tipsInCents: number = 0;
	paymentInProcess = false;
	private repositoryService = inject(RepositoryService);
	private modalCtrl: ModalController = inject(ModalController);
	private smoothrSocketService = inject(SmoothrSocketService);
	private destroyRef = inject(DestroyRef);
	private translate = inject(TranslateService);

	constructor() {}

	static async show(
		modalCtrl: ModalController,
		order: Order,
		paySessionStatistics: PaySessionStatistics | null,
		preSelectedTip: TipSelectorValue = { value: 0, type: TipType.RELATIVE, icon: '' },
		giftCards: PiggyGiftCard[] = []
	): Promise<CashPaymentResult> {
		const modal = await modalCtrl.create({
			component: CashPaymentModalComponent,
			cssClass: 'payment-sheet-modal',
			componentProps: {
				order,
				paySessionStatistics,
				preSelectedTip,
				giftCards,
			},
		});

		await modal.present();
		const { data } = await modal.onDidDismiss();
		return data;
	}

	ngOnInit(): void {
		this.cashDroHint = this.translate.instant('cashPayment.hintScan');
		const timeoutAt = this.order?.createdAt.add(15, 'minute')!;
		interval(1000)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				takeWhile(() => !this.paymentInProcess)
			)
			.subscribe(() => {
				const now = dayjs();
				const diff = timeoutAt.diff(now);
				const diffDayjs = dayjs(diff);
				this.timeoutIn = diffDayjs.format('mm:ss');
				if (now.isAfter(timeoutAt)) {
					this.paymentInProcess = true;
					this.paymentTimeout();
				}
			});
		this.smoothrSocketService
			.cashDroEvents(this.order?._id ?? '')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(event => {
				console.log('CashDroEvent', this.order?._id, event);
				this.cashDroEvent = event;
				switch (event) {
					case CashDroEvents.IN_PAYMENT:
					case CashDroEvents.SCANNED:
						this.cashDroHint = this.translate.instant('cashPayment.hintFollow');
						this.paymentInProcess = true;
						break;
					case CashDroEvents.CANCELLED:
						this.cashDroHint = this.translate.instant('cashPayment.hintCancelled');
						break;
					case CashDroEvents.CHECK_AVAILABILITY_FAILED:
						this.cashDroHint = this.translate.instant('cashPayment.hintCancelled');
						break;
					case CashDroEvents.PAID:
						this.paymentInProcess = true;
						this.cashDroHint = this.translate.instant('cashPayment.hintSuccess');
						this.paymentSuccess();
						break;
					case CashDroEvents.FAILED:
						this.paymentInProcess = true;
						this.cashDroHint = this.translate.instant('cashPayment.hintFailed');
						this.paymentFailed();
						break;
				}
			});
		this.reloadPrices();
	}

	paymentSuccess() {
		if (!this.cancelEnabled) {
			return;
		}
		this.cancelEnabled = false;
		setTimeout(() => {
			this.modalCtrl.dismiss(CashPaymentResult.SUCCESS).catch(() => {});
		}, 2_000);
	}

	paymentTimeout() {
		if (!this.cancelEnabled) {
			return;
		}
		this.cancelEnabled = false;
		setTimeout(() => {
			this.modalCtrl.dismiss(CashPaymentResult.TIMEOUT).catch(() => {});
		}, 2_000);
	}

	paymentFailed() {
		if (!this.cancelEnabled) {
			return;
		}
		this.cancelEnabled = false;
		setTimeout(() => {
			this.modalCtrl.dismiss(CashPaymentResult.FAILED).catch(() => {});
		}, 2_000);
	}

	reloadPrices() {
		const selectedTotalPrice = this.paySessionStatistics?.selectedTotalPrice ?? 0;
		const tipValue = this.paySessionStatistics?.tipsValue ?? 0;
		this.tipsInCents = +(tipValue * 100).toFixed(0);
		this.totalPrice = selectedTotalPrice;
		this.formattedTotalPrice = this.repositoryService.currencyFormat.format(this.totalPrice);
		this.formattedTips = this.repositoryService.currencyFormat.format(tipValue);
		// The CashDro scans this QR. It now carries a JSON payload so the CashDro side can
		// create the gift-card + cash payments (multiple) itself, mirroring the online flows.
		// tip is the absolute value in major units (e.g. euros); giftCards carry the hash the
		// backend redeems and the balance the CashDro uses to compute the remaining cash due.
		this.qrData = JSON.stringify({
			orderId: this.order?._id ?? '',
			paymentMethod: PaymentMethod.CASH,
			tip: tipValue,
			giftCards: (this.giftCards ?? []).map(card => ({
				hash: card.hash,
				balance: card.balance,
			})),
		});
		console.log({
			qrData: this.qrData,
		});
	}

	paymentCancelled() {
		if (!this.cancelEnabled) {
			return;
		}
		this.cancelEnabled = false;
		this.smoothrSocketService.cancelPayment(this.order?._id ?? '');
		this.modalCtrl.dismiss(CashPaymentResult.CANCELLED);
	}
}
